import { RiAddLine, RiEyeLine, RiTextSnippet } from "@remixicon/react";
import classNames from "classnames";
import React, { useMemo, useState } from "react";
import {
    ActionContextType, FieldTypedValue, FormField, FormFieldDescriptorInput,
    WorkflowTriggerType,
} from "../../../../__generated__/api";
import FoqalLogo from "../../../../assets/img/foqal-reverse-big.svg";
import ExternalForms from "../../../../components/pickers/external-forms/ExternalForms";
import { CUSTOM_FIELD_PROPS } from "../../../../components/pickers/field-type/FieldType";
import Button from "../../../../components/theme/button/Button";
import ButtonTabs from "../../../../components/theme/button/ButtonTabs";
import CallToAction from "../../../../components/theme/call-to-action/CallToAction";
import Icon from "../../../../components/theme/icon/Icon";
import SortableList from "../../../../components/theme/sortable/SortableList";
import Text from "../../../../components/theme/text/Text";
import { useCustomFieldModal } from "../custom-field/CustomFieldV3";
import Styles from "./Form.module.scss";
import Avatar from "../../../../components/avatar/Avatar";
import FieldTypeValueDisplay from "../../../../components/form-field/FieldTypeValueDisplay";
import FormGroup from "../../../../components/theme/form-group/FormGroup";
import Alert from "../../../../components/theme/alert/Alert";
import { HtmlRenderer } from "../../../../components/html-render/HtmlRenderer";
import { AutomationFlowFields } from "./RequestFormV3";
import { ACTION } from "../../../../components/workflow-v3/workflow/constants";
import { AutomationTab, FormDesignerValues } from "../../../../components/workflow-v3/providers/WorkflowModalProvider";
import ViewAutomation from "./ViewAutomation";

enum Tabs {
    CUSTOM = "CUSTOM",
    EXTERNAL = "EXTERNAL"
}
const TABS = [
    {
        key: Tabs.CUSTOM,
        text: "Custom Form",
    },
    {
        key: Tabs.EXTERNAL,
        text: "External Form",
    },
];

const LOADING_ROW = (
    <div
        className={classNames(Styles["field-row"], "loading")}
    >
        <Icon.Draggable variant="tertiary" />
        <div className={Styles["field-item"]}>
            <Text
                textVariant="body-2-regular"
                colorVariant="body-secondary"
                className={Styles["field-item-label"]}
            >
                Loading field
            </Text>
            <Text textVariant="body-2-medium" className={Styles["field-item-box"]}>
                <Icon.Send size="small" variant="secondary" />
                {" "}
                Loading...
            </Text>
        </div>
        <Button.Edit
            iconVariant="secondary"
        />
        <Button.Delete iconVariant="secondary" />
    </div>
);
export default function FormDesigner({
    fields,
    externalFormId,
    onChange,
    formFields,
    isLoading,
    userGroupsIds,
    automationFlowFields,
    isTabbed,
    triggerType,
}: FormDesignerValues & {
    onChange: (value: FormDesignerValues) => void;
    formFields: FormField[];
    isLoading?: boolean;
    userGroupsIds?: string[];
    automationFlowFields?: AutomationFlowFields;
    isTabbed?: boolean;
    triggerType: WorkflowTriggerType;
}) {
    const [formTab, setFormTab] = useState<string>(externalFormId ? Tabs.EXTERNAL : Tabs.CUSTOM);
    const [fieldPreview, setFieldPreview] = useState<unknown[]>([]);
    const [sidebarTab, setSidebarTab] = useState<string>("SETUP");
    const [showWorkflow, setShowWorkflow] = useState(false);
    const fieldList = useMemo(() => fields?.map(field => ({
        field,
        id: field.fieldId || field.requestorText || field.agentText as string,
    })) ?? [], [fields]);

    const fieldMap = useMemo(() => formFields?.toIdMap("id"), [formFields]);

    const deleteField = (index: number) => {
        const copy = fields ? [...fields] : [];
        copy.splice(index, 1);
        onChange({
            fields: copy,
            externalFormId,
        });
    };

    const { element: customFieldElement, onNew, onSetEdit } = useCustomFieldModal({
        userGroupsIds,
        onSave: (value: FormFieldDescriptorInput, field: FormField) => {
            const copy = fields ? [...fields] : [];
            const index = copy.findIndex(row => row.fieldId === value.fieldId);
            if (index >= 0) {
                copy[index] = value;
            } else {
                copy.push(value);
            }
            onChange({
                fields: copy,
                externalFormId,
            });
            fieldMap[field.id] = field;
        },
    });
    const currentTab = isLoading ? undefined : formTab;

    const hasRequestCreatingStep = useMemo(() => automationFlowFields?.workflow?.steps?.some(
        step => step.actionId === ACTION.REQUEST_CREATING,
    ), [automationFlowFields]);

    let preview;
    if (externalFormId) {
        preview = (
            <Alert
                variant="disabled"
                prefixIcon={<Icon.Alert />}
            >
                Sorry, Foqal does not currently support previewing external forms.
            </Alert>
        );
    } else if (isLoading) {
        preview = (
            <>
                <FormGroup label="Load" type="text" disabled />
                <FormGroup label="Loading123" type="text" disabled />
                <FormGroup label="Loading" type="text" disabled />
            </>
        );
    } else if (fields?.length === 0) {
        preview = (
            <Text as="p" className={Styles["empty-preview"]} textVariant="body-2-regular" colorVariant="body-secondary">
                No fields added to the form.
                {" "}
                <Button.Add onClick={onNew} variant="ghost-gray" size="small">
                    Add Field
                </Button.Add>
                {" "}
                to see a preview.
            </Text>
        );
    }

    return (
        <div className={Styles["form-designer-container"]}>
            {isTabbed && (
                <>
                    <Text as="h2" textVariant="subtitle-2">Form Setup</Text>
                    <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                        Select one of the fields from the list below.
                    </Text>
                    <ButtonTabs
                        values={[
                            { key: "SETUP", text: "Setup" },
                            { key: "PREVIEW", text: "Preview" },
                        ]}
                        onChange={item => setSidebarTab(item.key)}
                        value={sidebarTab}
                        size="small"
                        className={Styles["sidebar-tabs"]}
                    />
                </>
            )}
            <div className={classNames({
                [Styles["form-creation"]]: !isTabbed,
                [Styles["form-creation-tabbed"]]: isTabbed,
            })}
            >
                <div
                    className={classNames(
                        Styles["form-designer"],
                        { [Styles["panel-hidden"]]: isTabbed && sidebarTab !== "SETUP" },
                    )}
                >
                    {
                        !isTabbed && (
                            <>
                                <Text as="h2" textVariant="subtitle-2">Form Creation</Text>
                                <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                                    Select one of the fields from the list below.
                                </Text>
                                <ButtonTabs
                                    values={TABS}
                                    onChange={item => setFormTab(item.key)}
                                    value={formTab}
                                    className={Styles["form-designer-type"]}
                                />
                            </>
                        )
                    }

                    {isLoading && (
                        <div className={classNames(Styles["field-list"], Styles["field-loading"])}>
                            {LOADING_ROW}
                            {LOADING_ROW}
                            {LOADING_ROW}
                        </div>
                    )}
                    {currentTab === Tabs.EXTERNAL && (
                        <div>
                            <Text as="p" textVariant="body-2-regular" colorVariant="body-secondary">
                                If you are using an integration that supports forms, attach the external form to this
                                request type instead of creating the form in Agent directly.
                            </Text>
                            <ExternalForms
                                isClearable
                                value={externalFormId}
                                onSelected={value => {
                                    onChange({
                                        externalFormId: value,
                                        fields,
                                    });
                                }}
                            />

                        </div>
                    )}
                    {currentTab === Tabs.CUSTOM && (
                        <div className={Styles.form}>
                            {externalFormId ? (
                                <CallToAction
                                    icon={<Icon.Alert background="primary" variant="primary" size="medium" />}
                                    title="External Form Attached"
                                    text="You have attached an external form to this request type.
                                    You can't add custom fields to this form."
                                    actions={(
                                        <Button.Delete
                                            onClick={() => onChange({
                                                externalFormId: undefined,
                                                fields,
                                            })}
                                            variant="outline-blue"
                                        >
                                            Remove External Form
                                        </Button.Delete>
                                    )}
                                />
                            ) : (
                                <>
                                    {customFieldElement}
                                    <Button onClick={onNew} prefixIcon={<RiAddLine />} variant="outline-blue" size="medium">
                                        Add Field
                                    </Button>

                                    <SortableList
                                        id="list-fields"
                                        className={Styles["field-list"]}
                                        items={fieldList}
                                        onReorder={items => {
                                            onChange({
                                                fields: items.toIdList("field"),
                                                externalFormId,
                                            });
                                        }}
                                    >
                                        {fieldList.map((item, index) => {
                                            const field = fieldMap[item.id];
                                            if (!field && !item.field.requestorText && !item.field.agentText) {
                                                return null;
                                            }
                                            const isText = item.field.requestorText || item.field.agentText;

                                            let typeProperties;
                                            if (item.field.requestorText) {
                                                typeProperties = {
                                                    icon: <RiTextSnippet />,
                                                    text: "Requester Text",
                                                };
                                            } else if (item.field.agentText) {
                                                typeProperties = {
                                                    icon: <RiTextSnippet />,
                                                    text: "Agent Text",
                                                };
                                            } else {
                                                typeProperties = CUSTOM_FIELD_PROPS[field.type];
                                            }
                                            const id = item.id ?? item.field.requestorText ?? item.field.agentText;
                                            return (
                                                <SortableList.Item
                                                    shouldDragWholeElement={false}
                                                    className={Styles["field-row"]}
                                                    item={item}
                                                    id={id}
                                                    key={id}
                                                >
                                                    <SortableList.Listener>
                                                        <Icon.Draggable variant="tertiary" />
                                                    </SortableList.Listener>
                                                    <div className={Styles["field-item"]}>
                                                        {!isText && (
                                                            <Text
                                                                textVariant="body-2-regular"
                                                                colorVariant="body-secondary"
                                                                className={Styles["field-item-label"]}
                                                            >
                                                                {item.field?.name || field.name}
                                                                {!item.field?.isRequired && !field.isRequired && " (optional)"}
                                                            </Text>
                                                        )}
                                                        <Text textVariant="body-2-medium" className={Styles["field-item-box"]}>
                                                            <Icon size="small" variant="secondary" icon={typeProperties.icon} />
                                                            {" "}
                                                            {typeProperties.text}
                                                        </Text>
                                                    </div>
                                                    <Button.Edit
                                                        onClick={() => onSetEdit(item.field, field)}
                                                        iconVariant="secondary"
                                                    />
                                                    <Button.Delete iconVariant="secondary" onClick={() => deleteField(index)} />
                                                </SortableList.Item>

                                            );
                                        })}
                                    </SortableList>
                                    {
                                        triggerType === WorkflowTriggerType.RequestType && (
                                            <CallToAction
                                                icon={<Icon.Automation background="warning" variant="warning" size="medium" />}
                                                title="Dynamic Field Rules"
                                                text="Automations triggered during request creation by the
                                user to conditionally show or hide fields based on predefined rules."
                                                actions={hasRequestCreatingStep ? (
                                                    <Button.Edit
                                                        variant="outline-blue"
                                                        onClick={() => setShowWorkflow(true)}
                                                    >
                                                        Edit Dynamic Field Rules
                                                    </Button.Edit>
                                                ) : (
                                                    <Button.Add
                                                        variant="outline-blue"
                                                        onClick={() => setShowWorkflow(true)}
                                                    >
                                                        Add Dynamic Field Rules
                                                    </Button.Add>
                                                )}
                                            />
                                        )
                                    }

                                </>
                            )}
                        </div>
                    )}

                </div>
                <div
                    className={classNames(Styles.preview, "editable-background", {
                        [Styles["panel-hidden"]]: isTabbed && sidebarTab !== "PREVIEW",
                    })}
                >
                    <div className={Styles["preview-header"]}>
                        <RiEyeLine />
                        {" "}
                        Form Preview
                    </div>
                    <div className={Styles.form}>
                        <div className={Styles["form-header"]}>
                            <Avatar src={FoqalLogo} isCircle={false} className={Styles["form-icon"]} />
                            <Text as="h1" textVariant="subtitle-2" className={Styles["form-title"]}>New Ticket</Text>
                            <Button.Close iconVariant="default" />
                        </div>
                        <div className={classNames(Styles["form-body"], {
                            loading: isLoading,
                            [Styles["field-loading"]]: isLoading,
                        })}
                        >
                            {preview ?? (
                                fields?.map((field, index) => {
                                    const formField = fieldMap[field.fieldId];
                                    if (field.requestorText) {
                                        return (
                                            <div>
                                                <HtmlRenderer html={field.requestorText} />
                                            </div>
                                        );
                                    } if (!formField) {
                                        return null;
                                    }
                                    return (
                                        <FormGroup
                                            key={field.fieldId}
                                            label={field.name || formField.name}
                                            isOptional={!field.isRequired}
                                            helpText={field.description || formField.description}
                                            helpDisplay="default"
                                        >
                                            <FieldTypeValueDisplay
                                                inputClassName="form-control form-control-sm"
                                                shouldShowFieldName={false}
                                                shouldShowDescription={false}
                                                field={{
                                                    ...formField,
                                                    isRequired: false,
                                                }}
                                                value={fieldPreview[index] as FieldTypedValue}
                                                onChange={value => {
                                                    const copy = fieldPreview ? [...fieldPreview] : [];
                                                    copy[index] = value;
                                                    setFieldPreview(copy);
                                                }}
                                            />
                                        </FormGroup>
                                    );
                                })
                            )}
                        </div>
                        <div className={Styles["form-footer"]}>
                            <Button variant="outline-gray">Close</Button>
                            <Button variant="primary">Save</Button>
                        </div>

                    </div>
                </div>
                {
                    showWorkflow && (
                        <ViewAutomation
                            workflowId={automationFlowFields?.workflow?.id}
                            onHide={() => {
                                setShowWorkflow(false);
                            }}
                            startTab={AutomationTab.AUTOMATION}
                            form={{
                                formId: automationFlowFields?.workflow?.id,
                                formType: WorkflowTriggerType.RequestType,
                            }}
                            contextType={ActionContextType.Request}
                        />
                    )
                }
            </div>
        </div>
    );
}

FormDesigner.defaultProps = {
    isLoading: false,
    userGroupsIds: undefined,
    automationFlowFields: undefined,
    isTabbed: false,
};
